import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  createRow: { display: 'flex', gap: '8px', marginBottom: '20px' },
  input: {
    flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db',
    fontSize: '14px', outline: 'none', fontFamily: 'inherit',
  },
  createBtn: {
    padding: '10px 20px', borderRadius: '8px', border: 'none',
    backgroundColor: '#0052CC', color: '#fff', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  keyRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 0', borderBottom: '1px solid #f3f4f6',
  },
  keyName: { fontSize: '14px', fontWeight: '700', color: '#111827' },
  keyMeta: { fontSize: '12px', color: '#6b7280', marginTop: '2px' },
  keyPrefix: { fontFamily: 'monospace', fontSize: '12px', color: '#374151', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' },
  revokeBtn: {
    padding: '6px 14px', borderRadius: '6px', border: '1px solid #fca5a5',
    backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '12px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  revokedBadge: {
    fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px',
    backgroundColor: '#f3f4f6', color: '#9ca3af',
  },
  newKeyModal: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300,
  },
  newKeyCard: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
    maxWidth: '520px', width: '90%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
  },
  keyBox: {
    fontFamily: 'monospace', fontSize: '13px', backgroundColor: '#f3f4f6',
    padding: '14px 16px', borderRadius: '8px', wordBreak: 'break-all',
    marginBottom: '16px', border: '1px solid #e5e7eb',
  },
  warningBox: {
    fontSize: '12px', color: '#d97706', backgroundColor: '#fffbeb',
    border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px',
    marginBottom: '20px',
  },
  copyBtn: {
    padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
    backgroundColor: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', marginRight: '8px',
  },
  doneBtn: {
    padding: '8px 20px', borderRadius: '8px', border: 'none',
    backgroundColor: '#0052CC', color: '#fff', fontSize: '13px', fontWeight: '700',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  empty: { fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '20px' },
  upgradeBox: {
    padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb',
    borderRadius: '12px', border: '1px dashed #d1d5db',
  },
  codeBlock: {
    backgroundColor: '#111827', color: '#e5e7eb', borderRadius: '8px',
    padding: '16px 18px', fontSize: '12px', fontFamily: 'monospace',
    overflowX: 'auto', marginTop: '12px', lineHeight: '1.7',
  },
};

export default function Developer() {
  const { currentUser, userProfile } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const isPro = userProfile?.plan === 'professional' || userProfile?.plan === 'enterprise';

  useEffect(() => {
    if (isPro) fetchKeys();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  async function fetchKeys() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/keys?owner_id=${currentUser.uid}`);
      const data = await res.json();
      setKeys(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!newKeyName.trim()) {
      setError('Please enter a name for the key.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner_id: currentUser.uid, name: newKeyName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create key');
      setNewKey(data);
      setNewKeyName('');
      fetchKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(keyId) {
    if (!window.confirm('Revoke this API key? Any scripts using it will stop working immediately.')) return;
    try {
      await fetch(`${API}/api/keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner_id: currentUser.uid }),
      });
      fetchKeys();
    } catch {
      setError('Failed to revoke key.');
    }
  }

  function copyKey() {
    navigator.clipboard.writeText(newKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!isPro) {
    return (
      <AppLayout>
        <div style={styles.header}>
          <div style={styles.title}>Developer</div>
          <div style={styles.sub}>Programmatic access to V2VLab via REST API</div>
        </div>
        <div style={styles.upgradeBox}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔑</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
            REST API access requires Professional
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
            Generate API keys and automate simulations, agents, and scenarios programmatically.
          </div>
          <a href="/pricing" style={{
            display: 'inline-block', padding: '10px 24px', borderRadius: '8px',
            backgroundColor: '#0052CC', color: '#fff', fontSize: '14px', fontWeight: '700',
            textDecoration: 'none',
          }}>
            Upgrade to Professional →
          </a>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={styles.header}>
        <div style={styles.title}>Developer</div>
        <div style={styles.sub}>Manage API keys for programmatic access to V2VLab</div>
      </div>

      {error && (
        <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '16px' }}>{error}</div>
      )}

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Create New API Key</div>
        <div style={styles.createRow}>
          <input
            style={styles.input}
            placeholder="Key name (e.g. Python analysis script)"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            maxLength={100}
          />
          <button style={styles.createBtn} onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : '+ Generate Key'}
          </button>
        </div>

        <div style={styles.sectionTitle}>Your API Keys</div>
        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : keys.length === 0 ? (
          <div style={styles.empty}>No API keys yet. Create one above to get started.</div>
        ) : (
          keys.map(key => (
            <div key={key.id} style={styles.keyRow}>
              <div>
                <div style={styles.keyName}>{key.name}</div>
                <div style={styles.keyMeta}>
                  <span style={styles.keyPrefix}>{key.key_prefix}</span>
                  {' · Created '}{new Date(key.created_at).toLocaleDateString()}
                  {key.last_used_at && ` · Last used ${new Date(key.last_used_at).toLocaleDateString()}`}
                  {!key.last_used_at && ' · Never used'}
                </div>
              </div>
              {key.revoked ? (
                <span style={styles.revokedBadge}>Revoked</span>
              ) : (
                <button style={styles.revokeBtn} onClick={() => handleRevoke(key.id)}>Revoke</button>
              )}
            </div>
          ))
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Documentation</div>
        <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', marginBottom: '12px' }}>
          Full endpoint reference, perturbation parameters, evaluation output guide, and code examples.
        </div>
        
        <a 
          href="/api-docs"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block', padding: '10px 20px', borderRadius: '8px',
            backgroundColor: '#0052CC', color: '#fff', fontSize: '14px',
            fontWeight: '700', textDecoration: 'none',
          }}
        >
          View API Reference →
        </a>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Quick Start</div>
        <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
          Include your API key in the <code>Authorization</code> header of every request:
        </div>
        <div style={styles.codeBlock}>
{`curl -X POST https://v2vlab-backend-24053985582.us-central1.run.app/api/v1/simulations \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"scenario_id": "..."}'`}
        </div>
      </div>

      {/* New key reveal modal */}
      {newKey && (
        <div style={styles.newKeyModal}>
          <div style={styles.newKeyCard}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
              🔑 Your new API key
            </div>
            <div style={styles.warningBox}>
              ⚠️ Copy this key now — for security, you won't be able to see it again. If you lose it, you'll need to generate a new one.
            </div>
            <div style={styles.keyBox}>{newKey.key}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button style={styles.copyBtn} onClick={copyKey}>
                {copied ? '✓ Copied' : '📋 Copy Key'}
              </button>
              <button style={styles.doneBtn} onClick={() => setNewKey(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
